import React from 'react';
import { Info, FileText, HelpCircle, Home, Award, AlertTriangle, Calculator, Calendar } from 'lucide-react';

/**
 * Компонент с информацией о налоге на имущество
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о налоге на имущество физических лиц в России
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Что такое налог на имущество физических лиц?
        </h3>
        <p>
          <strong>Налог на имущество физических лиц</strong> — это местный налог, который уплачивается собственниками недвижимости: квартир, домов, гаражей и других объектов недвижимого имущества. Данный налог регулируется главой 32 Налогового кодекса РФ и зачисляется в местные бюджеты по месту нахождения объекта недвижимости.
        </p>
        <p>
          С 1 января 2020 года все регионы России перешли на исчисление налога на имущество исходя из кадастровой стоимости объектов недвижимости. Ранее в некоторых регионах налог рассчитывался на основе инвентаризационной стоимости.
        </p>

        <h3 className="flex items-center gap-2">
          <Home className="w-5 h-5 text-indigo-500" />
          Объекты налогообложения
        </h3>
        <p>Налогом на имущество физических лиц облагаются следующие виды недвижимости:</p>
        <ul>
          <li>Жилые дома и жилые помещения (квартиры, комнаты)</li>
          <li>Гаражи и машино-места</li>
          <li>Единые недвижимые комплексы</li>
          <li>Объекты незавершенного строительства</li>
          <li>Иные здания, строения, сооружения, помещения</li>
        </ul>
        
        <p>
          Важно отметить, что <strong>земельные участки</strong> не облагаются налогом на имущество физических лиц. Для них предусмотрен отдельный налог — земельный налог. Однако в нашем калькуляторе мы предусмотрели возможность расчета и этого налога для удобства пользователей.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Порядок расчета налога
        </h3>
        <p>
          Налог рассчитывается по следующей формуле:
        </p>
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 my-4">
          <p className="font-mono font-medium text-center">
            Налог = Кадастровая стоимость × Налоговая ставка × Доля владения × (Период владения / 12)
          </p>
        </div>
        <p>
          При этом из кадастровой стоимости могут вычитаться налоговые вычеты, а для некоторых категорий граждан могут применяться льготы, полностью или частично освобождающие от уплаты налога.
        </p>

        <h4>Налоговая база (кадастровая стоимость)</h4>
        <p>
          Налоговой базой для исчисления налога на имущество физических лиц является кадастровая стоимость объекта недвижимости, указанная в Едином государственном реестре недвижимости (ЕГРН) по состоянию на 1 января года, являющегося налоговым периодом.
        </p>
        <p>
          Кадастровая стоимость приближена к рыночной стоимости объекта и определяется в результате государственной кадастровой оценки. Информацию о кадастровой стоимости можно получить:
        </p>
        <ul>
          <li>На портале Росреестра (rosreestr.gov.ru)</li>
          <li>В выписке из ЕГРН</li>
          <li>На портале Госуслуг</li>
          <li>В налоговом уведомлении</li>
        </ul>

        <h4>Налоговые ставки</h4>
        <p>
          Налоговые ставки устанавливаются муниципальными образованиями (городами, районами), но не могут превышать пределы, установленные Налоговым кодексом РФ:
        </p>
        <ul>
          <li>0,1% для жилых домов, квартир, комнат, гаражей, машино-мест, хозяйственных строений</li>
          <li>2% для коммерческой недвижимости и объектов стоимостью свыше 300 млн рублей</li>
          <li>0,5% для прочих объектов</li>
        </ul>
        <p>
          В Москве, Санкт-Петербурге и некоторых других регионах действует прогрессивная шкала налоговых ставок для жилья в зависимости от его кадастровой стоимости. Например, в Москве ставки составляют:
        </p>
        <ul>
          <li>0,1% — для объектов стоимостью до 10 млн рублей</li>
          <li>0,15% — для объектов стоимостью от 10 до 20 млн рублей</li>
          <li>0,2% — для объектов стоимостью от 20 до 50 млн рублей</li>
          <li>0,3% — для объектов стоимостью от 50 до 300 млн рублей</li>
          <li>2% — для объектов стоимостью свыше 300 млн рублей</li>
        </ul>
        
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Понижающие коэффициенты
          </h4>
          <p className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            В течение первых четырех лет после перехода региона на расчет налога по кадастровой стоимости применяются понижающие коэффициенты: 0,2, 0,4, 0,6, 0,8. С пятого года налог взимается в полном объеме.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Award className="w-5 h-5 text-green-500" />
          Налоговые льготы и вычеты
        </h3>
        <h4>Налоговые вычеты</h4>
        <p>
          Налоговым кодексом РФ предусмотрены налоговые вычеты, уменьшающие налоговую базу:
        </p>
        <ul>
          <li>Для квартир — на величину кадастровой стоимости 20 кв.м. общей площади</li>
          <li>Для комнат — на величину кадастровой стоимости 10 кв.м. площади</li>
          <li>Для жилых домов — на величину кадастровой стоимости 50 кв.м. общей площади</li>
          <li>Для единого недвижимого комплекса, в состав которого входит хотя бы одно жилое помещение — на 1 млн рублей</li>
        </ul>
        <p>
          Эти вычеты применяются автоматически ко всем соответствующим объектам недвижимости.
        </p>

        <h4>Льготные категории граждан</h4>
        <p>
          От уплаты налога на имущество физических лиц полностью освобождаются:
        </p>
        <ul>
          <li>Пенсионеры</li>
          <li>Инвалиды I и II групп</li>
          <li>Инвалиды с детства, дети-инвалиды</li>
          <li>Ветераны боевых действий</li>
          <li>Герои Советского Союза и Герои Российской Федерации</li>
          <li>Другие категории, указанные в ст. 407 Налогового кодекса РФ</li>
        </ul>
        
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Важное ограничение по льготам</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Льготы предоставляются только по одному объекту каждого вида (одна квартира, один дом, один гараж и т.д.) по выбору налогоплательщика. При этом льгота предоставляется в отношении объекта, не используемого в предпринимательской деятельности.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-indigo-500" />
          Сроки и порядок уплаты
        </h3>
        <p>
          Налоговым периодом по налогу на имущество физических лиц является календарный год.
        </p>
        <p>
          Налог необходимо уплатить не позднее 1 декабря года, следующего за истекшим налоговым периодом. То есть налог за 2023 год нужно уплатить до 1 декабря 2024 года.
        </p>
        <p>
          Физическим лицам нет необходимости самостоятельно рассчитывать сумму налога — это делают налоговые органы, которые направляют налогоплательщикам налоговые уведомления. Уведомления направляются не позднее 30 дней до наступления срока уплаты налога.
        </p>
        <p>
          Уплатить налог можно:
        </p>
        <ul>
          <li>Через личный кабинет налогоплательщика на сайте ФНС России (nalog.gov.ru)</li>
          <li>Через портал Госуслуг</li>
          <li>В отделениях банков</li>
          <li>С помощью мобильных приложений банков</li>
          <li>В личном кабинете на сайте или в мобильном приложении банка</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-red-500" />
          Часто задаваемые вопросы
        </h3>
        
        <h4>Что делать, если я не согласен с кадастровой стоимостью?</h4>
        <p>
          Если вы считаете, что кадастровая стоимость вашего объекта недвижимости завышена, вы можете оспорить ее. Для этого можно:
        </p>
        <ol>
          <li>Обратиться в комиссию по рассмотрению споров о результатах определения кадастровой стоимости при Росреестре</li>
          <li>Обратиться в суд с административным исковым заявлением</li>
        </ol>
        <p>
          Для успешного оспаривания кадастровой стоимости обычно требуется отчет об оценке рыночной стоимости объекта недвижимости, подготовленный независимым оценщиком.
        </p>

        <h4>Как получить налоговую льготу?</h4>
        <p>
          Для получения налоговой льготы необходимо подать заявление в налоговый орган. К заявлению нужно приложить документы, подтверждающие право на льготу (пенсионное удостоверение, справку об инвалидности и т.д.).
        </p>
        <p>
          Заявление можно подать:
        </p>
        <ul>
          <li>Лично в налоговой инспекции</li>
          <li>Через личный кабинет налогоплательщика на сайте ФНС России</li>
          <li>По почте</li>
          <li>Через МФЦ</li>
        </ul>
        <p>
          После получения заявления налоговый орган сам выберет объект с максимальной суммой налога для предоставления льготы. Если вы хотите самостоятельно выбрать объект, по которому будет предоставляться льгота, необходимо указать это в заявлении.
        </p>

        <h4>Что делать, если я не получил налоговое уведомление?</h4>
        <p>
          Налоговые уведомления не направляются в следующих случаях:
        </p>
        <ul>
          <li>Если сумма налога меньше 100 рублей</li>
          <li>Если у налогоплательщика есть льгота, полностью освобождающая его от уплаты налога</li>
          <li>Если налогоплательщик использует личный кабинет на сайте ФНС и не выбрал способ получения уведомлений на бумаге</li>
        </ul>
        <p>
          Если ни одно из этих условий не выполняется, а уведомление не получено, рекомендуется обратиться в налоговый орган по месту нахождения объекта недвижимости.
        </p>

        <h4>Могу ли я не платить налог, если недвижимость не используется?</h4>
        <p>
          Факт неиспользования объекта недвижимости не является основанием для освобождения от уплаты налога. Налогом на имущество физических лиц облагается право собственности на объект недвижимости, вне зависимости от того, используется он или нет.
        </p>
        <p>
          Исключением могут быть случаи, когда объект недвижимости признан аварийным и подлежащим сносу или реконструкции. В таких случаях нужно обратиться в налоговый орган с соответствующими документами.
        </p>

        <h4>Что происходит при продаже или покупке недвижимости в течение года?</h4>
        <p>
          Налог на имущество исчисляется с учетом количества месяцев владения объектом в течение налогового периода (календарного года).
        </p>
        <p>
          При продаже недвижимости налог рассчитывается до месяца прекращения права собственности (включительно). При этом если право собственности прекращено до 15-го числа включительно, то за этот месяц налог не начисляется. Если после 15-го числа — месяц учитывается при расчете налога.
        </p>
        <p>
          При покупке недвижимости налог начисляется начиная с месяца возникновения права собственности. Если право собственности возникло до 15-го числа включительно, то месяц учитывается при расчете налога. Если после 15-го числа — налог за этот месяц не начисляется.
        </p>

        <h4>Как облагается налогом имущество, находящееся в общей собственности?</h4>
        <p>
          В случае долевой собственности (когда определены доли каждого собственника) налог рассчитывается для каждого собственника пропорционально его доле.
        </p>
        <p>
          В случае совместной собственности (без определения долей, например, у супругов) налог распределяется между собственниками в равных долях.
        </p>

        <h4>Может ли налог на имущество измениться в течение года?</h4>
        <p>
          Сумма налога может измениться в случае:
        </p>
        <ul>
          <li>Изменения кадастровой стоимости объекта (в результате оспаривания или переоценки)</li>
          <li>Изменения налоговых ставок (изменения в нормативных актах)</li>
          <li>Приобретения или утраты права на налоговую льготу</li>
          <li>Изменения доли владения объектом</li>
        </ul>
        <p>
          В большинстве случаев изменения применяются начиная с нового налогового периода (следующего года).
        </p>

        <h4>Как налог на имущество связан с НДФЛ при продаже недвижимости?</h4>
        <p>
          Налог на имущество и НДФЛ при продаже недвижимости — это два разных налога:
        </p>
        <ul>
          <li>Налог на имущество физических лиц — ежегодный налог на право собственности на недвижимость</li>
          <li>НДФЛ при продаже недвижимости — налог на доход, полученный от продажи недвижимости</li>
        </ul>
        <p>
          НДФЛ уплачивается однократно в случае продажи недвижимости и может не взиматься, если объект находился в собственности дольше минимального срока владения (3-5 лет в зависимости от ситуации) или если доход от продажи не превышает налоговый вычет (1 млн рублей для жилой недвижимости, 250 тыс. рублей для иной недвижимости).
        </p>

        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-500" />
          Ответственность за неуплату налога
        </h3>
        <p>
          За неуплату или несвоевременную уплату налога на имущество физических лиц предусмотрена ответственность:
        </p>
        <ul>
          <li>Начисление пени в размере 1/300 ставки рефинансирования ЦБ РФ за каждый день просрочки платежа</li>
          <li>Штраф в размере 20% от неуплаченной суммы налога (если неуплата произошла непреднамеренно)</li>
          <li>Штраф в размере 40% от неуплаченной суммы налога (если доказана умышленная неуплата)</li>
        </ul>
        <p>
          В случае длительной неуплаты налога налоговый орган может обратиться в суд для принудительного взыскания задолженности. После решения суда взыскание может производиться из заработной платы, пенсии, со счетов в банках, а также путем ареста и реализации имущества должника.
        </p>

        <h3 className="flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-500" />
          Изменения в налогообложении имущества физических лиц
        </h3>
        <p>
          В последние годы система налогообложения имущества физических лиц претерпела значительные изменения:
        </p>
        <ul>
          <li>Переход от инвентаризационной к кадастровой стоимости в качестве налоговой базы</li>
          <li>Введение налоговых вычетов (на площадь объектов недвижимости)</li>
          <li>Установление понижающих коэффициентов на переходный период</li>
          <li>Уточнение перечня льготных категорий граждан</li>
        </ul>
        <p>
          Основная цель этих изменений — приведение налогообложения недвижимости в соответствие с рыночными реалиями и обеспечение более справедливого распределения налоговой нагрузки.
        </p>
        <p>
          В настоящее время обсуждаются дальнейшие изменения, направленные на совершенствование системы налогообложения имущества физических лиц, в том числе:
        </p>
        <ul>
          <li>Уточнение методики определения кадастровой стоимости</li>
          <li>Расширение перечня объектов, по которым предоставляются налоговые вычеты</li>
          <li>Совершенствование механизма предоставления налоговых льгот</li>
        </ul>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium">Как снизить налоговую нагрузку законным способом</h4>
          <ul className="text-green-600 dark:text-green-400 text-sm mb-0">
            <li>Узнайте, имеете ли вы право на налоговые льготы и своевременно подайте заявление</li>
            <li>Проверьте кадастровую стоимость объекта и оспорьте ее, если она завышена</li>
            <li>Используйте налоговые вычеты на площадь объекта недвижимости</li>
            <li>Разделите крупный объект на несколько меньших (если это возможно и экономически целесообразно)</li>
            <li>Переоформите недвижимость на члена семьи, имеющего право на налоговые льготы</li>
          </ul>
        </div>

        <p className="font-semibold mt-6 text-lg text-center">
          Используйте наш калькулятор налога на имущество, чтобы заранее рассчитать сумму налога и спланировать свои расходы!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;